import {
  Box,
  Flex,
  Tab,
  TabIndicator,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { DateSelector, MovieCard, MovieCardListSkeleton } from "../../modules";

import { MovieTabProps } from "../../@types";
import { AlertMessage } from "../../components";
import DynamicGrid from "../../components/DynamicGrid";
import { NO_MOVIE } from "../../constant/alertMessage";

function MovieGridWithTabs({
  tabs,
  tabsProps,
  tabListProps,
  tabProps,
  showTabIndicator = false,
  tabIndicatorProps,
  tabPanelsProps,
  tabPanelProps,
  mainBoxProps,
  alertMessageProps,
  dynamicGridProps,
  ...props
}: MovieTabProps) {
  // TabIndicator measures the active tab's offsetWidth once on mount. If a
  // custom @font-face for the tab label is still loading at that point, the
  // measurement uses the fallback font's (narrower) metrics, so the pill
  // doesn't fully cover the tab once the real font swaps in. Remount the
  // indicator after all fonts are ready so it re-measures against final text.
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts) return;
    document.fonts.ready.then(() => setFontsReady(true));
  }, []);

  return (
    <Box {...mainBoxProps}>
      <Tabs
        onChange={(tabIndex: number) => {
          const tab = tabs[tabIndex];
          tab && tab.onTabChange && tab.onTabChange();
        }}
        variant="unstyled"
        display="flex"
        flexDirection="column"
        alignItems="center"
        isLazy
        {...tabsProps}
      >
        <TabList
          {...tabListProps}
          sx={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "surface.500",
            color: "white",
            rounded: "full",
            // Safety net: if a tab label still doesn't fit at some viewport
            // width despite responsive sizing, scroll within the pill
            // instead of overflowing the page horizontally.
            maxWidth: "100%",
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            ...(tabListProps?.sx || {}),
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                lineHeight: 1,
                rounded: "full",
                transition: "all 0.3s ease",
                fontWeight: "bold",
              }}
              _selected={{
                background: showTabIndicator ? "transparent" : "primary.500",
              }}
              px="2rem"
              py={{
                base: "0.5rem",
                xl: "1rem",
              }}
              key={`movie__tab__gird__tab+${index}`}
              fontSize={{
                base: "18px",
                xl: "26px",
              }}
              {...tabProps}
            >
              {tab.icon && (
                <Flex mr="5px" alignItems="center" justifyContent="center">
                  {tab.icon}
                </Flex>
              )}
              <Box as="span" lineHeight={1}>
                {tab.title}
              </Box>
            </Tab>
          ))}
          {showTabIndicator && (
            <TabIndicator
              key={fontsReady ? "tab-indicator-ready" : "tab-indicator-loading"}
              top={0}
              bottom={0}
              zIndex={0}
              bg="primary.500"
              rounded="full"
              {...tabIndicatorProps}
            />
          )}
        </TabList>
        <TabPanels {...tabPanelsProps}>
          {tabs.map((tab, index) => (
            <TabPanel key={`tab__pane__${index}`} {...tabPanelProps}>
              {tab?.showDateSelector && tab?.onDateChange && (
                <Flex
                  justifyContent={{
                    base: "center",
                    md:
                      tab.dateSelectorAlignment === "left"
                        ? "flex-start"
                        : tab.dateSelectorAlignment === "center"
                          ? "center"
                          : "flex-end",
                  }}
                  gap={{
                    base: "1rem",
                    md: "0",
                  }}
                  wrap="wrap"
                  width="100%"
                  mt="2rem"
                  {...tab.dateFilterProps}
                >
                  <DateSelector
                    noOfDays={tab.dateSelectorMaxDays || 5}
                    onDateChange={tab.onDateChange}
                    {...props}
                  />
                </Flex>
              )}
              {tab.isLoading ? (
                <MovieCardListSkeleton />
              ) : !tab.movies || tab.movies?.length === 0 ? (
                <AlertMessage
                  message={NO_MOVIE}
                  sx={{
                    mt: "1rem",
                  }}
                  {...alertMessageProps}
                />
              ) : (
                <DynamicGrid
                  key={`movie__tab__gird__panes+${index}`}
                  baseChildKey={`movie_grid_${index}`}
                  dynamicGridProps={dynamicGridProps}
                >
                  {tab.movies?.map((movie, movieIndex: number) => (
                    <MovieCard
                      key={`movie__card__panes__${movieIndex}`}
                      {...props}
                      {...movie}
                    />
                  ))}
                </DynamicGrid>
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
}

export default MovieGridWithTabs;
